import { Icons } from '@/components/icons';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInfiniteFilterList } from '../hooks/use-infinite-filter-list';
import type { GlobalFilterItem } from '../types';
import {
  GlobalFilerListItemSkeleton,
  GlobalFilterListItem,
} from './filter-list-item';

export function FilterSegments({
  search,
  onToggle,
  filteredItems,
}: {
  search: string;
  onToggle: (id: string, displayString: string) => void;
  filteredItems: GlobalFilterItem[];
}) {
  const {
    items: segments,
    isLoading,
    hasNextPage,
    sentryRef,
  } = useInfiniteFilterList(
    useInfiniteQuery(
      api.objects_segment.query.infinite.queryOptions({
        params: {
          pageSize: 25,
          search: search || undefined,
        },
      })
    )
  );

  if (isLoading) {
    return Array.from({ length: 10 }).map((_, index) => (
      <GlobalFilerListItemSkeleton key={index}>
        <Skeleton className='h-3 w-40' />
      </GlobalFilerListItemSkeleton>
    ));
  }

  if (!segments.length) {
    return (
      <div className='flex h-full flex-col'>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Icons.segment />
            </EmptyMedia>
            <EmptyTitle>Inga segment hittades</EmptyTitle>
            <EmptyDescription>
              Vi kunde inte hitta några segment.{' '}
              {search.length > 0 && 'Försök med en annan sökterm.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {segments.map((segment) => {
        const isSelected = Boolean(
          filteredItems.find(
            (fi) => fi.type === 'global_segments' && fi.id === segment.id
          )
        );

        return (
          <GlobalFilterListItem
            onToggle={onToggle}
            isSelected={isSelected}
            key={segment.id}
            item={segment}
          >
            <div className='mt-1 flex items-center gap-2'>
              <p className='text-xs'>
                {segment.realestates?.length ?? 0} fastighet(er)
              </p>
            </div>
          </GlobalFilterListItem>
        );
      })}

      {hasNextPage && (
        <div ref={sentryRef}>
          <GlobalFilerListItemSkeleton>
            <Skeleton className='h-3 w-40' />
          </GlobalFilerListItemSkeleton>
        </div>
      )}
    </div>
  );
}
